import { z } from 'zod';
export const contentStateSchema = z.enum(['draft', 'published', 'offline']);
export const releaseStatusSchema = z.enum([
    'queued',
    'building',
    'deploying',
    'active',
    'superseded',
    'failed',
]);
export const leadStatusSchema = z.enum(['pending', 'contacted', 'converted', 'invalid']);
export const qrTypeSchema = z.enum([
    'wecom_live',
    'personal_wechat',
    'mini_program',
    'official_account',
]);
export const ctaTargetSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('external'), url: z.url() }),
    z.object({ kind: z.literal('route'), path: z.string().startsWith('/') }),
    z.object({
        kind: z.literal('qr'),
        slot: z.string().min(1).max(100),
        image: z.string().optional(),
    }),
    z.object({ kind: z.literal('anchor'), id: z.string().min(1).max(100) }),
]);
const textItemSchema = z.object({ title: z.string().max(80), text: z.string().max(500) });
const courseBlockBase = z.object({
    title: z.string().min(1).max(80),
    anchor: z.string().regex(/^[a-z][a-z0-9-]*$/),
    sort: z.number().int().nonnegative(),
});
export const courseBlockSchema = z.discriminatedUnion('type', [
    courseBlockBase.extend({
        type: z.literal('list'),
        data: z.object({
            items: z.array(z.string().max(200)),
            emphasisCards: z.array(textItemSchema).max(3),
        }),
    }),
    courseBlockBase.extend({
        type: z.literal('grid'),
        data: z.object({
            columns: z.union([z.literal(2), z.literal(3), z.literal(4)]),
            cards: z.array(textItemSchema),
            dashedLast: z.boolean(),
        }),
    }),
    courseBlockBase.extend({
        type: z.literal('steps'),
        data: z.object({ steps: z.array(textItemSchema).max(12) }),
    }),
    courseBlockBase.extend({
        type: z.literal('table'),
        data: z.object({
            columns: z.array(z.string().max(80)),
            rows: z.array(z.array(z.string().max(200))),
            fixedLeft: z.boolean(),
            highlightColumn: z.number().int().nonnegative().nullable(),
        }),
    }),
    courseBlockBase.extend({
        type: z.literal('ref'),
        data: z.object({
            className: z.string().max(80).optional(),
            limit: z.number().int().min(1).max(12),
        }),
    }),
]);
export const seoSchema = z.object({
    title: z.string().min(1).max(70),
    description: z.string().min(1).max(180),
    ogImage: z.string().url().optional(),
});
export const qrConfigSchema = z.object({
    image: z.string(),
    title: z.string().max(60),
    description: z.string().max(160),
});
const emptyQrConfig = {
    image: '',
    title: '微信扫码，添加规划老师',
    description: '课程咨询 · 内测名额 · 测评入口，一对一回复',
};
export const qrOverrideSchema = z
    .object({
    enabled: z.boolean(),
    config: qrConfigSchema,
})
    .default({ enabled: false, config: emptyQrConfig });
export const moduleQrSettingsSchema = z
    .object({ defaultQr: qrConfigSchema })
    .default({ defaultQr: emptyQrConfig });
export const courseSchema = z
    .object({
    id: z.string(),
    code: z.string().regex(/^[a-z][a-z0-9-]*$/),
    name: z.string().min(1).max(40),
    badge: z.string().max(20),
    positioning: z.string().max(80),
    tags: z.array(z.string().max(30)).max(8),
    audience: z.string().max(160),
    highlights: z.array(z.string().max(80)).max(8),
    price: z.number().int().nonnegative(),
    originalPrice: z.number().int().nonnegative().nullable(),
    cover: z.string(),
    sort: z.number().int().nonnegative(),
    sidebarNote: z.string().max(120),
    qrOverride: qrOverrideSchema,
    blocks: z.array(courseBlockSchema),
    seo: seoSchema,
})
    .superRefine((course, context) => {
    const anchors = new Set();
    for (const block of course.blocks) {
        if (anchors.has(block.anchor))
            context.addIssue({
                code: 'custom',
                path: ['blocks'],
                message: `块锚点 ${block.anchor} 重复`,
            });
        anchors.add(block.anchor);
    }
});
export const materialSchema = z.object({
    id: z.string(),
    slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    title: z.string().min(1).max(80),
    summary: z.string().max(160),
    updated: z.string().max(30),
    claimed: z.string().max(20),
    cover: z.string(),
    body: z.string(),
    includedTitle: z.string().min(1).max(40).default('包含内容'),
    included: z.array(z.string().max(100)),
    suitableForTitle: z.string().min(1).max(40).default('适合谁'),
    suitableFor: z.array(z.string().max(100)),
    howToClaimTitle: z.string().min(1).max(40).default('如何领取'),
    howToClaim: z.array(z.string().max(100)),
    qrCopy: z.string().max(100),
    qrOverride: qrOverrideSchema,
    sort: z.number().int().nonnegative(),
    seo: seoSchema,
});
export const scoreSchema = z.object({
    l: z.string(),
    r: z.string(),
    w: z.string(),
    s: z.string(),
    o: z.string(),
});
const caseShotSchema = z.object({
    image: z.string(),
    category: z.string().max(30),
    caption: z.string().max(200),
});
export const caseStudySchema = z.object({
    id: z.string().regex(/^[a-z0-9-]+$/),
    name: z.string().max(30),
    meta: z.string().max(80),
    weeks: z.string().max(80),
    className: z.string().max(50),
    from: z.string().max(8),
    to: z.string().max(8),
    focus: z.string().max(20),
    teacher: z.string().max(30),
    tag: z.string().max(30),
    after: scoreSchema,
    before: scoreSchema,
    date: z.string().max(30),
    enroll: z.string().max(30).default(''),
    quote: z.string().max(200),
    backgroundTitle: z.string().max(80).default('学员情况介绍'),
    background: z.string().max(500),
    pathTitle: z.string().max(80).default('学习过程'),
    path: z.array(textItemSchema).max(8),
    reviewTitle: z.string().max(80).default('老师点评'),
    review: z.string().max(500),
    galleryTitle: z.string().max(80).default('学习群实录'),
    galleryDescription: z
        .string()
        .max(240)
        .default('学员基础、学习过程、老师指导、班主任督学到提分报喜，均来自学员学习群真实记录 · 已获学员授权'),
    shots: z.array(caseShotSchema).max(20).default([]),
    reportTitle: z.string().max(80).default('雅思成绩单'),
    reportImage: z.string().optional(),
    qrOverride: qrOverrideSchema,
    sort: z.number().int().nonnegative(),
    seo: seoSchema,
});
export const teacherAvatarCropSchema = z.object({
    sourceImage: z.string().min(1),
    imageWidth: z.number().int().positive(),
    imageHeight: z.number().int().positive(),
    zoom: z.number().min(1).max(4),
    x: z.number().min(-100).max(100),
    y: z.number().min(-100).max(100),
});
export const teacherSchema = z.object({
    id: z.string(),
    name: z.string().max(30),
    score: z.string().max(50),
    subject: z.string().max(50),
    education: z.string().max(100),
    role: z.string().max(100),
    painPoints: z.array(z.string().max(40)).length(2),
    description: z.string().max(500),
    image: z.string(),
    avatarCrop: teacherAvatarCropSchema.optional(),
    lead: z.boolean(),
    leadProfile: z
        .object({
        headline: z.string().max(100),
        teachingTag: z.string().max(30),
        serviceValue: z.string().max(30),
        serviceLabel: z.string().max(50),
        credentials: z.array(z.string().max(120)).max(6),
        experience: z.array(z.string().max(160)).max(8),
    })
        .default({
        headline: '',
        teachingTag: '',
        serviceValue: '',
        serviceLabel: '',
        credentials: [],
        experience: [],
    }),
    qrOverride: qrOverrideSchema,
    sort: z.number().int().nonnegative(),
});
export const qrSlotSchema = qrConfigSchema.extend({
    slot: z.string().min(1).max(100),
    type: qrTypeSchema.default('wecom_live'),
    channelState: z.string().max(100).optional(),
    leadFallback: z.boolean().default(false),
});
export const brandSchema = z.object({
    name: z.string().min(1).max(20),
    logo: z.string().max(500),
    aiPlatformUrl: z.url(),
    contact: z.string().max(100),
    leadSuccessMessage: z.string().max(120),
    footerDescription: z.string().max(240),
    companyName: z.string().max(80),
    filingNumber: z.string().max(80),
    userAgreementUrl: z.string().max(500),
    privacyPolicyUrl: z.string().max(500),
    analytics: z.object({ src: z.url(), siteId: z.string().max(100) }).optional(),
    defaultQr: qrSlotSchema,
});
export const homeSchema = z.object({
    hero: z.object({
        eyebrow: z.string().max(80),
        title: z.string().max(80),
        highlightText: z.string().max(20).default('AI'),
        description: z.string().max(240),
        primaryCta: z.string().max(40),
        primaryTarget: ctaTargetSchema.default({ kind: 'qr', slot: 'home-consult' }),
        secondaryCta: z.string().max(40),
        secondaryTarget: ctaTargetSchema.default({ kind: 'anchor', id: 'sec-course' }),
        trust: z.string().max(100),
    }),
    stats: z
        .array(z.object({
        value: z.number(),
        suffix: z.string().max(10),
        title: z.string().max(30),
        description: z.string().max(100),
    }))
        .max(4),
    aiTitle: z.string().max(60),
    aiDescription: z.string().max(240),
    aiProducts: z
        .array(z.object({
        key: z.string().max(30),
        badge: z.string().max(20).default(''),
        name: z.string().max(30),
        subtitle: z.string().max(50),
        description: z.string().max(180),
        tags: z.array(z.string().max(20)).max(6),
        cta: z.string().max(30),
        target: ctaTargetSchema,
    }))
        .length(4),
    productDetail: z.object({
        eyebrow: z.string().max(30),
        title: z.string().max(80),
        description: z.string().max(180),
    }),
    writingFeatures: z
        .array(z.object({
        title: z.string().max(80),
        description: z.string().max(300),
    }))
        .max(6),
    speakingFeatures: z
        .array(z.object({
        title: z.string().max(80),
        description: z.string().max(300),
    }))
        .max(6),
    chatWall: z.object({
        title: z.string().max(40),
        description: z.string().max(100),
        rating: z.string().max(30),
        images: z.array(z.string().max(500)).max(20),
    }),
    brandTrust: z.object({
        intro: z.string().max(500),
        milestones: z.array(z.object({ year: z.string().max(10), text: z.string().max(160) })).max(12),
        awards: z
            .array(z.object({
            organization: z.string().max(50),
            title: z.string().max(100),
            year: z.string().max(30),
        }))
            .max(12),
        certificates: z
            .array(z.object({ title: z.string().max(100), thumbnail: z.string(), image: z.string() }))
            .max(12),
    }),
    process: z
        .array(z.object({ title: z.string().max(30), items: z.array(z.string().max(80)).max(5) }))
        .max(5),
    faqs: z.array(z.tuple([z.string().max(80), z.string().max(300)])).max(12),
    faqIntro: z.object({
        description: z.string().max(180),
        bullets: z.array(z.string().max(100)).max(5),
        primaryCta: z.string().max(40).default('咨询课程顾问'),
        primaryTarget: ctaTargetSchema.default({ kind: 'qr', slot: 'home-consult' }),
        secondaryCta: z.string().max(40),
        secondaryTarget: ctaTargetSchema.default({ kind: 'anchor', id: 'sec-lead' }),
    }),
    lead: z.object({
        title: z.string().max(80),
        description: z.string().max(180),
        button: z.string().max(30),
        hint: z.string().max(120),
    }),
    activity: z.object({
        enabled: z.boolean(),
        tag: z.string().max(20),
        text: z.string().max(100),
        button: z.string().max(30),
        target: ctaTargetSchema,
    }),
    qrCodes: z
        .object({
        teacher: qrConfigSchema,
        consult: qrConfigSchema,
        bottom: qrConfigSchema,
    })
        .default({
        teacher: emptyQrConfig,
        consult: emptyQrConfig,
        bottom: emptyQrConfig,
    }),
});
export const aboutSchema = z.object({
    eyebrow: z.string().max(40),
    title: z.string().max(60),
    intro: z.string().max(300),
    stats: z.array(z.object({ value: z.string().max(20), label: z.string().max(50) })).max(4),
    identity: z.string().max(500),
    milestones: z.array(z.object({ year: z.string().max(20), text: z.string().max(120) })).max(10),
    partnership: z.object({
        title: z.string().max(80),
        note: z.string().max(160),
        items: z.array(z.string().max(160)).max(8),
        disclaimer: z.string().max(200),
    }),
    principles: z.array(textItemSchema).max(4),
    methods: z.array(textItemSchema).max(8),
    chat: z.object({
        title: z.string().max(40),
        description: z.string().max(240),
        images: z.array(z.string().max(500)).max(10),
        footnote: z.string().max(160),
    }),
    support: z.string().max(500),
    certificates: z
        .array(z.object({ title: z.string().max(100), thumbnail: z.string(), image: z.string() }))
        .max(12),
    awards: z
        .array(z.object({
        organization: z.string().max(50),
        title: z.string().max(100),
        year: z.string().max(30),
    }))
        .max(12),
    office: z.object({ address: z.string().max(160), images: z.array(z.string()).length(3) }),
});
export const navigationSchema = z
    .array(z.object({
    label: z.string().max(30),
    href: z.string().max(500),
    disabled: z.boolean().optional(),
}))
    .max(12);
export const footerSchema = z
    .array(z.object({
    title: z.string().max(30),
    links: z.array(z.object({ label: z.string().max(40), href: z.string().max(500) })).max(12),
}))
    .max(8);
export const siteSnapshotSchema = z.object({
    releaseId: z.string(),
    generatedAt: z.string(),
    brand: brandSchema,
    home: homeSchema,
    courses: z.array(courseSchema),
    materials: z.array(materialSchema),
    cases: z.array(caseStudySchema),
    teachers: z.array(teacherSchema),
    qrSlots: z.array(qrSlotSchema),
    courseSettings: moduleQrSettingsSchema,
    materialSettings: moduleQrSettingsSchema,
    caseSettings: moduleQrSettingsSchema,
    teacherSettings: moduleQrSettingsSchema,
    about: aboutSchema,
    navigation: navigationSchema,
    footer: footerSchema,
});
export const leadInputSchema = z
    .object({
    name: z.string().trim().min(1).max(40),
    contact: z.string().trim().min(2).max(64),
    sourceSlot: z.string().trim().min(1).max(100),
    goalScore: z.enum(['6.0', '6.5', '7.0', '7.5+', '还不确定']).optional(),
    examTime: z.enum(['1 个月内', '1–3 个月', '3–6 个月', '还没定']).optional(),
    website: z.string().trim().max(0).optional(),
})
    .superRefine((input, context) => {
    const compact = input.contact.replace(/[\s+-]/g, '');
    if (/^\d{7,}$/.test(compact) && !/^1\d{10}$/.test(compact)) {
        context.addIssue({ code: 'custom', path: ['contact'], message: '手机号格式不正确' });
    }
});
export { AI_PLATFORM_URL, applyCanonicalAiPlatformUrl } from './ai-platform.js';
export { initialSiteSnapshot } from './seed.js';
export { getTeacherAvatarStyle } from './teacher-images.js';
//# sourceMappingURL=index.js.map